import { getInstagramUsers, GetInstagramUsersParams, InstagramUser } from '@round/api';
import { DataState } from 'App.types';
import { useCallback, useState } from 'react';

type State = DataState<{ users: InstagramUser[]; hasNextPage: boolean | null }>;

const initialState: State = {
    data: null,
    error: null,
    status: 'idle',
};

export function useInstagramUsers() {
    const [state, setState] = useState<State>(initialState);

    const fetchData = useCallback(async (params: GetInstagramUsersParams, requestInit?: RequestInit) => {
        setState((prevState) => ({
            data: prevState.data,
            error: null,
            status: 'loading',
        }));

        try {
            const response = await getInstagramUsers(params, requestInit);

            if (response.status === 404) {
                setState((prevState) => ({
                    data: prevState.data,
                    error: response.data.detail,
                    status: 'error',
                }));
                return response;
            }

            setState((prevState) => ({
                data: {
                    users: (prevState.data?.users ?? []).concat(response.data.results),
                    hasNextPage: !!response.data.next,
                },
                error: null,
                status: 'success',
            }));
            return response;
        } catch (e) {
            if (e instanceof Error && e.name === 'AbortError') {
                setState((prevState) => ({
                    data: prevState.data,
                    error: null,
                    status: 'idle',
                }));
                return;
            }

            const errorMessage = e instanceof Error ? e.message : 'Could not get instagram users';
            setState((prevState) => ({
                data: prevState.data,
                error: errorMessage,
                status: 'error',
            }));
            throw e;
        }
    }, []);

    const reset = () => setState(initialState);

    return {
        state,

        fetchData,
        reset,
    };
}
